import React from "react";
import type { SplitText as SplitTextPlugin } from "gsap/SplitText";
type AnimationRefs = {
  categoriesRef: React.RefObject<HTMLDivElement | null>;
  productsRef: React.RefObject<HTMLDivElement | null>;
  quickLinksRef: React.RefObject<HTMLDivElement | null>;
};
export function useDropdownAnimation() {
  const gsapRef = React.useRef<GSAP | null>(null);
  const timelineRef = React.useRef<GSAPTimeline | null>(null);
  const productsTimelineRef = React.useRef<GSAPTimeline | null>(null);
  const splitInstancesRef = React.useRef<SplitTextPlugin[]>([]);
  const productsSplitInstancesRef = React.useRef<SplitTextPlugin[]>([]);
  const SplitTextRef = React.useRef<typeof SplitTextPlugin | null>(null);

  // Load gsap + SplitText dynamically on client side only
  React.useEffect(() => {
    if (typeof window !== "undefined" && !gsapRef.current) {
      Promise.all([import("gsap"), import("gsap/SplitText")]).then(([gsapMod, splitMod]) => {
        gsapRef.current = gsapMod.gsap;
        SplitTextRef.current = splitMod.SplitText;
        gsapMod.gsap.registerPlugin(splitMod.SplitText);
      });
    }
  }, []);
  const animateInitial = React.useCallback((refs: AnimationRefs) => {
    const gsap = gsapRef.current;
    if (!gsap) return;
    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    // Revert any previous split text instances
    splitInstancesRef.current.forEach(split => split.revert());
    splitInstancesRef.current = [];
    const {
      categoriesRef,
      productsRef,
      quickLinksRef
    } = refs;
    const tl = gsap.timeline({
      defaults: {
        ease: "power3.out"
      }
    });
    timelineRef.current = tl;
    const SplitText = SplitTextRef.current;
    if (categoriesRef.current) {
      const categoryDivider = categoriesRef.current.querySelector("[data-category-divider]");
      const categoryLines = categoriesRef.current.querySelectorAll("[data-category-line]");
      const categoryTextContainers = categoriesRef.current.querySelectorAll("[data-category-text]");
      if (categoryDivider) {
        tl.fromTo(categoryDivider, {
          scaleY: 0,
          transformOrigin: "top center"
        }, {
          scaleY: 1,
          duration: 0.9,
          ease: "cubic-bezier(0.125, 0.425, 0.27, 1)"
        }, 0.15);
      }

      // Animate category text with SplitText
      if (categoryTextContainers.length > 0 && SplitText) {
        categoryTextContainers.forEach((container, index) => {
          const textElements = container.querySelectorAll("[data-split-text]");
          textElements.forEach(textEl => {
            const split = new SplitText(textEl, {
              type: "lines"
            });
            splitInstancesRef.current.push(split);
            tl.fromTo(split.lines, {
              y: "100%",
              opacity: 0
            }, {
              y: "0%",
              opacity: 1,
              duration: 0.6,
              ease: "cubic-bezier(0.125, 0.425, 0.27, 1)",
              stagger: 0.05
            }, 0.3 + index * 0.12);
          });
        });
      }
      if (categoryLines.length > 0) {
        tl.fromTo(categoryLines, {
          scaleX: 0,
          transformOrigin: "left center"
        }, {
          scaleX: 1,
          duration: 0.6,
          ease: "cubic-bezier(0.125, 0.425, 0.27, 1)",
          stagger: 0.12
        }, 0.5);
      }
    }
    if (productsRef.current) {
      const productDivider = productsRef.current.querySelector("[data-product-divider]");
      const productLines = productsRef.current.querySelectorAll("[data-product-line]");
      const productTextContainers = productsRef.current.querySelectorAll("[data-product-text]");
      const productImages = productsRef.current.querySelectorAll("[data-product-image]");
      if (productDivider) {
        tl.fromTo(productDivider, {
          scaleY: 0,
          transformOrigin: "top center"
        }, {
          scaleY: 1,
          duration: 0.9,
          ease: "cubic-bezier(0.125, 0.425, 0.27, 1)"
        }, 0.25);
      }
      if (productLines.length > 0) {
        tl.fromTo(productLines, {
          scaleX: 0,
          transformOrigin: "left center"
        }, {
          scaleX: 1,
          duration: 0.6,
          ease: "cubic-bezier(0.125, 0.425, 0.27, 1)",
          stagger: 0.1
        }, 0.4);
      }

      // Animate product images with fade in up
      if (productImages.length > 0) {
        tl.fromTo(productImages, {
          y: 20,
          opacity: 0
        }, {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: "cubic-bezier(0.125, 0.425, 0.27, 1)",
          stagger: 0.08
        }, 0.5);
      }

      // Animate product text with SplitText
      if (productTextContainers.length > 0 && SplitText) {
        productTextContainers.forEach((container, index) => {
          const textElements = container.querySelectorAll("[data-split-text]");
          textElements.forEach(textEl => {
            const split = new SplitText(textEl, {
              type: "lines"
            });
            productsSplitInstancesRef.current.push(split);
            tl.fromTo(split.lines, {
              y: "100%",
              opacity: 0
            }, {
              y: "0%",
              opacity: 1,
              duration: 0.5,
              ease: "cubic-bezier(0.125, 0.425, 0.27, 1)",
              stagger: 0.05
            }, 0.6 + index * 0.1);
          });
        });
      }
    }
    if (quickLinksRef.current) {
      const quickLinkTitle = quickLinksRef.current.querySelector("[data-quicklink-title]");
      const quickLinkItems = quickLinksRef.current.querySelectorAll("[data-quicklink-item]");
      if (quickLinkTitle) {
        tl.fromTo(quickLinkTitle, {
          y: 15,
          opacity: 0
        }, {
          y: 0,
          opacity: 1,
          duration: 0.4
        }, 0.35);
      }
      if (quickLinkItems.length > 0) {
        tl.fromTo(quickLinkItems, {
          y: 15,
          opacity: 0
        }, {
          y: 0,
          opacity: 1,
          duration: 0.4,
          stagger: 0.05
        }, 0.45);
      }
    }
  }, []);
  const animateProducts = React.useCallback((productsRef: React.RefObject<HTMLDivElement | null>) => {
    const gsap = gsapRef.current;
    if (!gsap) return;
    if (productsTimelineRef.current) {
      productsTimelineRef.current.kill();
    }

    // Revert any previous product split text instances
    productsSplitInstancesRef.current.forEach(split => split.revert());
    productsSplitInstancesRef.current = [];
    if (!productsRef.current) return;
    const productDivider = productsRef.current.querySelector("[data-product-divider]");
    const productLines = productsRef.current.querySelectorAll("[data-product-line]");
    const productTextContainers = productsRef.current.querySelectorAll("[data-product-text]");
    const productImages = productsRef.current.querySelectorAll("[data-product-image]");
    if (productLines.length === 0 && productTextContainers.length === 0) return;
    const tl = gsap.timeline({
      defaults: {
        ease: "power3.out"
      }
    });
    productsTimelineRef.current = tl;
    const SplitText = SplitTextRef.current;
    if (productDivider) {
      tl.fromTo(productDivider, {
        scaleY: 0,
        transformOrigin: "top center"
      }, {
        scaleY: 1,
        duration: 0.8,
        ease: "cubic-bezier(0.125, 0.425, 0.27, 1)"
      }, 0.1);
    }
    if (productLines.length > 0) {
      tl.fromTo(productLines, {
        scaleX: 0,
        transformOrigin: "left center"
      }, {
        scaleX: 1,
        duration: 0.5,
        ease: "cubic-bezier(0.125, 0.425, 0.27, 1)",
        stagger: 0.09
      }, 0.2);
    }

    // Animate product images with fade in up when switching categories
    if (productImages.length > 0) {
      tl.fromTo(productImages, {
        y: 20,
        opacity: 0
      }, {
        y: 0,
        opacity: 1,
        duration: 0.5,
        ease: "cubic-bezier(0.125, 0.425, 0.27, 1)",
        stagger: 0.07
      }, 0.25);
    }

    // Animate product text with SplitText when switching categories
    if (productTextContainers.length > 0 && SplitText) {
      productTextContainers.forEach((container, index) => {
        const textElements = container.querySelectorAll("[data-split-text]");
        textElements.forEach(textEl => {
          const split = new SplitText(textEl, {
            type: "lines"
          });
          productsSplitInstancesRef.current.push(split);
          tl.fromTo(split.lines, {
            y: "100%",
            opacity: 0
          }, {
            y: "0%",
            opacity: 1,
            duration: 0.5,
            ease: "cubic-bezier(0.125, 0.425, 0.27, 1)",
            stagger: 0.05
          }, 0.35 + index * 0.09);
        });
      });
    }
  }, []);
  React.useEffect(() => {
    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      if (productsTimelineRef.current) {
        productsTimelineRef.current.kill();
      }
      // Clean up split text instances
      splitInstancesRef.current.forEach(split => split.revert());
      productsSplitInstancesRef.current.forEach(split => split.revert());
      splitInstancesRef.current = [];
      productsSplitInstancesRef.current = [];
    };
  }, []);
  return {
    animateInitial,
    animateProducts
  };
}